import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { tenantRoleDtoSchema, tenantRoleListDtoSchema, type TenantRoleDto, type TenantRoleListDto } from '@lib/dto/tenant-role.dto'
import type { TenantRoleInput } from '@lib/input/tenant-role.input'
import type { ITenantRoleService } from '@/types/TenantRoleService.types'

/** The company's roles (docs/custom-roles.spec.md): organization admins only. */
class TenantRoleServiceImpl implements ITenantRoleService {
  async list(): Promise<TenantRoleListDto> {
    try {
      return await api.get('/roles', tenantRoleListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the roles', {}, error)
      throw error
    }
  }

  async get(id: string): Promise<TenantRoleDto> {
    try {
      return await api.get(`/roles/${id}`, tenantRoleDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a role', { id }, error)
      throw error
    }
  }

  async create(input: TenantRoleInput): Promise<TenantRoleDto> {
    try {
      return await api.post('/roles', input, tenantRoleDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create a role', {}, error)
      throw error
    }
  }

  async update(id: string, input: TenantRoleInput): Promise<TenantRoleDto> {
    try {
      return await api.put(`/roles/${id}`, input, tenantRoleDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to save a role', { id }, error)
      throw error
    }
  }

  async reset(id: string): Promise<TenantRoleDto> {
    try {
      return await api.post(`/roles/${id}/reset`, {}, tenantRoleDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to reset a role', { id }, error)
      throw error
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await api.del(`/roles/${id}`)
    } catch (error) {
      logRequestFailure('Failed to delete a role', { id }, error)
      throw error
    }
  }
}

export const tenantRoleService: ITenantRoleService = new TenantRoleServiceImpl()
